<template>
  <div class="vp-address-input">
    <HiddenInput input-id="id" />
    <div
      v-for="(input, i) in inputs"
      :key="i"
      class="address-input-row"
      :class="{
        'has-value': (countryType && input.binds.inputId === 'country') || stateHasType,
        dirty: isDirty(input.binds.inputId),
      }"
    >
      <component
        :is="input.component"
        v-bind="input.binds"
        :disabled="disabled"
        @change="onChange"
      />
      <div
        v-if="fieldHasErrors(input.binds.inputId)"
        class="invalid-feedback"
      >
        {{ fieldErrors(input.binds.inputId) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { HiddenInput, useFormsStore } from 'vuniform';
import { usaStates } from 'typed-usa-states';
import caProvinces from 'provinces-ca';
import countries from 'world-countries';
import VPCombobox from '@/components/form/VPCombobox.vue';
import VPInput from '@/components/form/VPInput.vue';
import { useDebounceFn } from '@vueuse/core';

const formsStore = useFormsStore();
const formId = inject('formId', '');

const emit = defineEmits<{
  (e: 'change', value?: unknown): void;
}>();

interface Props {
  disabled?: boolean;
  street2?: boolean;
}

defineProps<Props>();

const countryType = computed(() => formsStore.fieldGetValue(formId, 'address.country'));
const stateHasType = computed(() => !!formsStore.fieldGetValue(formId, 'address.state'));
const stateListExists = computed(() => countryType.value === 'US' || countryType.value === 'CA');

const stateProvinceOptions = computed(() => {
  if (countryType.value === 'US') {
    return usaStates.map(state => ({
      name: state.name,
      value: state.abbreviation,
    }));
  }
  return caProvinces.map(province => ({
    name: province.name,
    value: province.abbreviation,
  }));
});

const countryList = countries.map(country => ({
  name: country.name.common,
  value: country.cca2,
}));

const inputs = computed(() => {
  return [
    {
      component: VPInput,
      binds: { inputId: 'street1', placeholder: 'Add address line 1', label: 'Address line 1' },
    },
    {
      component: VPInput,
      binds: { inputId: 'street2', placeholder: 'Add address line 2', label: 'Address line 2' },
    },
    {
      component: VPInput,
      binds: { inputId: 'city', placeholder: 'Add city', label: 'City' },
    },
    {
      component: stateListExists.value ? VPCombobox : VPInput,
      binds: {
        inputId: 'state',
        placeholder: 'Select state',
        options: stateProvinceOptions.value,
        label: 'State',
      },
    },
    {
      component: VPInput,
      binds: { inputId: 'postalCode', placeholder: 'Add zip/postal code', label: 'Zip/Postal code' },
    },
    {
      component: VPCombobox,
      binds: {
        inputId: 'country',
        placeholder: 'Select country',
        options: countryList,
        label: 'Country',
      },
    },
  ];
});

const fieldHasErrors = (field: string) => {
  return formsStore.fieldHasErrors(formId, field);
};

const fieldErrors = (field: string) => {
  const errors = formsStore.fieldGetErrors(formId, field);
  return errors.join(' ');
};

const isDirty = (fieldName: string): boolean => {
  const fullFieldName = 'address.' + fieldName;
  return formsStore.isFieldDirty(formId, fullFieldName);
};

const onChange = useDebounceFn(() => {
  emit('change');
}, 300);
</script>
